import { useContext } from 'react';
import { useMatch, useNavigate } from 'react-router';

import { MIN_WINE_BOTTLE_QTY_TO_SHIP } from 'constants/shop';
import { GlobalContext } from 'contexts/GlobalContext';
import Button from 'honeydew/Button';
import { useAccountPlusMembershipHook } from 'hooks/useAccountPlusMembershipHook/useAccountPlusMembershipHook';
import useOrderMin from 'hooks/useOrderMin';
import { plulurizeWord } from 'utils/globalUtils';
import { getGroceryItems, getTotalWineBottleQty } from 'utils/orderUtils';
import { getCartSnapEligibleSubtotals, getProductsSubtotal, PriceType } from 'utils/priceUtils';

/**
 * @param {Object} props - The component props.
 * @param {Object} props.account - Account data from the server.
 * @param {Object} props.selections - Selections data from the server.
 * @param {Object} props.subscription - Subscription data from the server.
 */
function CartCTAs({ account, selections, subscription }) {
    const navigate = useNavigate();
    const isShopRoute = useMatch('/shop/*');

    const { setCartOpen, setEbtPaymentFlowOpen } = useContext(GlobalContext);

    const { plusMembershipEligibleOrder } = useAccountPlusMembershipHook();

    const groceryItems = getGroceryItems(selections);
    const { isOrderMinMet, orderMin } = useOrderMin();

    const priceType = plusMembershipEligibleOrder ? PriceType.PLUS_MEMBER : PriceType.DEFAULT;

    const groceryItemsTotalCost = getProductsSubtotal({
        options: { priceType },
        products: groceryItems,
    });

    const { snapEligibleSubtotal } = getCartSnapEligibleSubtotals({ selections });

    const diffToOrderMin = orderMin - groceryItemsTotalCost;
    const totalWineBottleQty = getTotalWineBottleQty(selections);
    const bottlesToMeetMin = MIN_WINE_BOTTLE_QTY_TO_SHIP - totalWineBottleQty;

    const navigateToShopping = () => {
        if (window.isNativeApp) {
            window.ReactNativeWebView?.postMessage(JSON.stringify({ navigate: 'Shop' }));
        } else {
            setCartOpen(false);
            if (!isShopRoute) {
                navigate('/shop');
            }
        }
    };

    return (
        <div className="mb-10 flex flex-col gap-10 bg-white px-15 py-20">
            {!isOrderMinMet && totalWineBottleQty < MIN_WINE_BOTTLE_QTY_TO_SHIP ? (
                <>
                    <Button onClick={navigateToShopping} size="md" variant="primary">
                        Return to Shop
                    </Button>

                    <p className="text-body-xs text-center">
                        Add <span className="font-grotesk-bold text-error">${diffToOrderMin.toFixed(2)}</span> to ship
                        Groceries{' '}
                        {subscription?.ebtCard?.last4 ? (
                            <>
                                or{' '}
                                <button className="underline" onClick={() => setEbtPaymentFlowOpen(true)} type="button">
                                    pay with SNAP EBT
                                </button>{' '}
                                to waive the order minimum
                            </>
                        ) : (
                            'order'
                        )}
                    </p>

                    {totalWineBottleQty > 0 && totalWineBottleQty < MIN_WINE_BOTTLE_QTY_TO_SHIP && (
                        <p className="text-body-xs text-center">
                            Add{' '}
                            <span className="font-grotesk-bold text-error">
                                {bottlesToMeetMin} {plulurizeWord('bottle', bottlesToMeetMin)}
                            </span>{' '}
                            to ship Wine order
                        </p>
                    )}
                </>
            ) : (
                <>
                    {account?.featureFlags?.featureSnapEnabled &&
                        !subscription?.ebtCard?.last4 &&
                        snapEligibleSubtotal > 0 && (
                            <Button onClick={() => setEbtPaymentFlowOpen(true)} size="md" variant="secondary">
                                <span className="flex items-center gap-10">
                                    <img
                                        className="w-[29px]"
                                        src="https://img-cdn.misfitsmarket.com/boysenberry/logos/snap-logo.svg"
                                        alt="SNAP-Eligible logo"
                                        width="29px"
                                        height="24px"
                                    />
                                    Make a payment with EBT
                                </span>
                            </Button>
                        )}
                    <p className="text-body-xs text-center text-grey">
                        All done shopping? Your order is automatically placed when your window closes, no checkout
                        needed!{' '}
                        {!subscription?.ebtCard?.last4 &&
                            account?.featureFlags?.featureSnapEnabled &&
                            'You may add an EBT payment anytime during your shopping window.'}
                    </p>
                    {subscription?.ebtCard?.last4 && (
                        <p className="text-body-xs text-center font-grotesk-bold">
                            If you&apos;re paying with SNAP EBT, be sure to manually apply your EBT payment before your
                            shopping window closes.
                        </p>
                    )}
                </>
            )}
        </div>
    );
}

export default CartCTAs;
